import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useInspectionStore } from "@/stores/inspection";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";
import { convertStructureUnitsToLocations, filterStructureUnitsByLegacyType } from "@/composables/spanBySpan";

export const useNstmStore = defineStore("nstmFatigue", () => {
  let pageData = ref({});
  const inspectionStore = useInspectionStore();
  const configStore = useConfigStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getNstmFatigue = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_NSTM_FATIGUE;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update NSTM Fatigue", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspModuleObject = (nstm, structure) => {
    structure.userInspection.complexFeaturePresent = nstm.complexFeaturePresent;
    structure.userInspection.fatigueDetails = nstm.fatigueDetails;
    structure.userInspection.nstmPresent = nstm.nstmPresent;
    structure.userInspection.nstmCondition = nstm.nstmCondition;
    structure.userInspection.redundancy = nstm.redundancy;
    structure.userBridge.deptMainMaterialType = nstm.deptMainMaterialType;
    structure.userBridge.subProblemType = nstm.subProblemType;
    structure.userBridge.subReconWorkType = nstm.subReconWorkType;
    structure.userBridge.supProblemType = nstm.supProblemType;
    structure.userBridge.estCumFatigLife = nstm.estCumFatigLife;
    structure.userBridge.estTruckTraffic = nstm.estTruckTraffic;
    structure.userBridge.estTruckTrafficYear = nstm.estTruckTrafficYear;
    structure.inspectionFCs = nstm.inspectionFCs;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_NSTM_FATIGUE.replace("{brkey}", brkey).replace(
      "{inspectionId}",
      inspectionId
    );

    await axios
      .get(url)
      .then((response) => {
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      brkey: structure.structureBridge.brkey,
      inspectionId: structure.inspection.inspectionId,
      complexFeaturePresent: structure.userInspection.complexFeaturePresent,
      fatigueDetails: structure.userInspection.fatigueDetails,
      nstmPresent: structure.userInspection.nstmPresent,
      nstmCondition: structure.userInspection.nstmCondition,
      redundancy: structure.userInspection.redundancy,
      deptMainMaterialType: structure.userBridge.deptMainMaterialType,
      subProblemType: structure.userBridge.subProblemType,
      subReconWorkType: structure.userBridge.subReconWorkType,
      supProblemType: structure.userBridge.supProblemType,
      estCumFatigLife: structure.userBridge.estCumFatigLife,
      estTruckTraffic: structure.userBridge.estTruckTraffic,
      estTruckTrafficYear: structure.userBridge.estTruckTrafficYear,
      inspectionFCs: structure.inspectionFCs,
      structureUnitSpanSets:
        structure.structureUnitSpanSets?.filter(
          (s) =>
            s.spanConfigDesignation?.startsWith("M") ||
            s.spanConfigDesignation?.startsWith("A") ||
            s.mainSpanSetIndicator === "Y"
        ) ?? [],
      locations: convertStructureUnitsToLocations(
        inspectionStore.getFilteredStructureUnits(
          [
            STRUCTURE_UNIT_TYPE.ABUTMENT,
            STRUCTURE_UNIT_TYPE.APPROACH,
            STRUCTURE_UNIT_TYPE.MAIN,
            STRUCTURE_UNIT_TYPE.PIER,
          ],
          true
        )
      ),
    };
  };

  const convertToSaveInspLinkObject = (nstm) => {
    return {
      brkey: nstm.brkey,
      inspectionId: nstm.inspectionId,
      complexFeaturePresent: nstm.complexFeaturePresent,
      fatigueDetails: nstm.fatigueDetails,
      nstmPresent: nstm.nstmPresent,
      nstmCondition: nstm.nstmCondition,
      redundancy: nstm.redundancy,
      deptMainMaterialType: nstm.deptMainMaterialType,
      subProblemType: nstm.subProblemType,
      subReconWorkType: nstm.subReconWorkType,
      supProblemType: nstm.supProblemType,
      estCumFatigLife: nstm.estCumFatigLife,
      estTruckTraffic: nstm.estTruckTraffic,
      estTruckTrafficYear: nstm.estTruckTrafficYear,
      inspectionFcs: nstm.inspectionFCs,
      structureUnits: nstm.structureUnits,
    };
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      complexFeaturePresent: response.complexFeaturePresent,
      fatigueDetails: response.fatigueDetails,
      nstmPresent: response.nstmPresent,
      nstmCondition: response.nstmCondition,
      redundancy: response.redundancy,
      deptMainMaterialType: response.deptMainMaterialType,
      subProblemType: response.subProblemType,
      subReconWorkType: response.subReconWorkType,
      supProblemType: response.supProblemType,
      estCumFatigLife: response.estCumFatigLife,
      estTruckTraffic: response.estTruckTraffic,
      estTruckTrafficYear: response.estTruckTrafficYear,
      inspectionFCs: response.inspectionFcs,
      structureUnits: response.structureUnits,
      structureUnitSpanSets: response.structureUnitSpanSets,
      locations: convertStructureUnitsToLocations(
        filterStructureUnitsByLegacyType(
          response.structureUnits,
          [
            STRUCTURE_UNIT_TYPE.ABUTMENT,
            STRUCTURE_UNIT_TYPE.APPROACH,
            STRUCTURE_UNIT_TYPE.MAIN,
            STRUCTURE_UNIT_TYPE.PIER,
          ],
          true
        )
      ),
    };
  };

  return {
    getNstmFatigue,
    saveData,
    loadDataObject,
    configStore,
    REFERENCE_TABLE,
    inspectionStore,
  };
});
